import { Clock, X } from 'lucide-react'
import { useRecordingStore } from '@/stores/recording'
import { runOnBannerContent, visibleRunOns } from '@/lib/run-on'

/**
 *   You have another recording running
 *   Started 2:15 pm on iOS · quiet for 22 min
 * (CLA-1564).
 *
 * Shown while THIS capture is live and the server says the same clinician has
 * another live recording that has gone quiet for 10+ minutes — most often one
 * left running in the previous room, which would otherwise record into the
 * next patient's consult.
 *
 * Deliberately PASSIVE (John's decision): no Stop, no Keep, and it never ends
 * anything. Running two devices in two rooms is a legitimate way to work; the
 * banner only makes sure it is a choice. Calm grey-green with a clock for the
 * same reason — it is information, not an alarm. The × hides it for the recordings it names
 * now; a new one, or one that drops off the list and comes back, shows again.
 *
 * Mounted in AppShell so it is visible on any page while recording.
 */
export function RunOnRecordingBanner() {
  const state = useRecordingStore((s) => s.state)
  const list = useRecordingStore((s) => s.otherLiveRecordings)
  const dismissed = useRecordingStore((s) => s.runOnDismissed)
  const dismiss = useRecordingStore((s) => s.dismissRunOnBanner)

  if (state !== 'recording' && state !== 'paused') return null
  const content = runOnBannerContent(visibleRunOns(list, dismissed))
  if (!content) return null

  return (
    <div
      role="status"
      data-testid="run-on-recording-banner"
      className="shrink-0 bg-claro-50 border-b border-claro-100 px-4 py-2 flex items-start gap-3"
    >
      <Clock className="w-4 h-4 mt-0.5 text-claro-600 shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-ink">{content.title}</p>
        <p className="text-xs text-ink-secondary">{content.detail}</p>
      </div>
      <button
        onClick={() => dismiss()}
        className="p-1 rounded text-ink-tertiary hover:text-ink hover:bg-claro-100 transition-colors shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  )
}
